(() => {
  'use strict';

  const CACHE = new Map();
  const ROOT_ID = 'ProductGridContainer';
  const FETCH_BATCH_SIZE = 2;
  let running = false;
  let observer;
  let timer;

  const qs = (root, selector) => root ? root.querySelector(selector) : null;

  function getRoot() {
    return document.getElementById(ROOT_ID);
  }

  function getGrid(root = getRoot()) {
    return qs(root, '#product-grid');
  }

  function getCurrentPage() {
    const raw = new URLSearchParams(window.location.search).get('page');
    const page = Number.parseInt(raw || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  function getBaseKey(grid) {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    return [
      window.location.pathname,
      params.toString(),
      grid.dataset.id || '',
      grid.dataset.cnProductsPerPage || '',
      grid.dataset.cnProductsCount || ''
    ].join('|');
  }

  function makeSectionUrl(sectionId, page) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  async function fetchSectionPage(sectionId, page) {
    const response = await fetch(makeSectionUrl(sectionId, page), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) {
      throw new Error('CNobre stock-first: failed to fetch page ' + page);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const pageRoot = doc.getElementById(ROOT_ID);

    if (!pageRoot) {
      throw new Error('CNobre stock-first: missing product grid on page ' + page);
    }

    return pageRoot;
  }

  function collectCards(root) {
    return Array.from(root.querySelectorAll('#product-grid > li[data-cn-available]'));
  }

  function stableStockFirst(cards) {
    const seen = new Set();
    const available = [];
    const soldOut = [];

    cards.forEach((card) => {
      const id = card.dataset.cnProductId || card.innerHTML;
      if (seen.has(id)) return;
      seen.add(id);

      if (card.dataset.cnAvailable === '1') available.push(card);
      else soldOut.push(card);
    });

    return available.concat(soldOut);
  }

  async function loadNeededCards(grid, root) {
    const pageSize = Math.max(1, Number.parseInt(grid.dataset.cnProductsPerPage || '16', 10));
    const productCount = Math.max(0, Number.parseInt(grid.dataset.cnProductsCount || '0', 10));
    const totalPages = Math.max(1, Math.ceil(productCount / pageSize));
    const currentPage = Math.min(getCurrentPage(), totalPages);
    const sectionId = grid.dataset.id;
    const targetEnd = currentPage * pageSize;

    if (!sectionId || totalPages === 1) {
      return {
        cards: stableStockFirst(collectCards(root)),
        pageSize,
        currentPage
      };
    }

    const cards = [];
    let page = 1;

    while (page <= totalPages) {
      const batchPages = [];
      for (let offset = 0; offset < FETCH_BATCH_SIZE && page + offset <= totalPages; offset += 1) {
        batchPages.push(page + offset);
      }

      const pageRoots = await Promise.all(
        batchPages.map((pageNumber) => {
          if (pageNumber === currentPage) return Promise.resolve(root.cloneNode(true));
          return fetchSectionPage(sectionId, pageNumber);
        })
      );

      pageRoots.forEach((pageRoot) => {
        cards.push(...collectCards(pageRoot));
      });

      const availableCount = cards.reduce(
        (count, card) => count + (card.dataset.cnAvailable === '1' ? 1 : 0),
        0
      );

      page += batchPages.length;

      if (availableCount >= targetEnd) break;
    }

    return {
      cards: stableStockFirst(cards),
      pageSize,
      currentPage
    };
  }

  function renderPage(grid, sortedCards, pageSize, currentPage) {
    const start = (currentPage - 1) * pageSize;
    const visible = sortedCards.slice(start, start + pageSize);

    const fragment = document.createDocumentFragment();
    visible.forEach((sourceCard, index) => {
      const card = sourceCard.cloneNode(true);
      card.style.setProperty('--index', String(index));
      fragment.appendChild(card);
    });

    grid.replaceChildren(fragment);
  }

  async function applyStockFirst() {
    const root = getRoot();
    const grid = getGrid(root);
    if (!root || !grid || running) return;

    const key = getBaseKey(grid);
    const page = getCurrentPage();
    const renderKey = key + '|page=' + page;

    if (grid.dataset.cnStockFirstApplied === renderKey) return;

    running = true;
    root.dataset.cnStockFirstLoading = '1';

    try {
      const cacheKey = renderKey;
      let payload = CACHE.get(cacheKey);

      if (!payload) {
        payload = await loadNeededCards(grid, root);
        CACHE.set(cacheKey, payload);
      }

      renderPage(grid, payload.cards, payload.pageSize, page);
      grid.dataset.cnStockFirstApplied = renderKey;
      grid.dataset.cnStockFirstAvailable = String(
        payload.cards.filter((card) => card.dataset.cnAvailable === '1').length
      );
      grid.dataset.cnStockFirstSoldout = String(
        payload.cards.filter((card) => card.dataset.cnAvailable !== '1').length
      );

      document.dispatchEvent(new CustomEvent('cnobre:stock-first-applied', {
        detail: {
          available: Number(grid.dataset.cnStockFirstAvailable),
          soldOut: Number(grid.dataset.cnStockFirstSoldout),
          page
        }
      }));
    } catch (error) {
      console.warn(error);
    } finally {
      delete root.dataset.cnStockFirstLoading;
      running = false;
    }
  }

  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(applyStockFirst, 80);
  }

  function start() {
    const root = getRoot();
    if (!root) return;

    observer = new MutationObserver(() => {
      if (running) return;
      const grid = getGrid(root);
      if (!grid) return;
      const key = getBaseKey(grid) + '|page=' + getCurrentPage();
      if (grid.dataset.cnStockFirstApplied === key) return;
      schedule();
    });

    observer.observe(root, { childList: true, subtree: true });
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', schedule);
  window.addEventListener('popstate', schedule);
})();